import { useState } from "react";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { ValidationQuestion } from "@/zapi-frontend/shared/const/validation-questions";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { AppButton, Typography } from "@libs/zapi-ui/custom";
import { CardHeader } from "@libs/zapi-ui/shadcn";
import { CheckIcon, ProhibitIcon, SparkleIcon, XIcon } from "@phosphor-icons/react";
import { CurrentStatusDropdown } from "./_CurrentStatusDropdown";

type TValidationStatsProps = {
  isCurrent?: boolean;
  questions: ValidationQuestion[];
  updateCurrent: (current: boolean) => void;
  validateAll: () => void;
  isValidating: boolean;
  currentlyValidatingIndex: number;
  isChapterContentReady: boolean;
};

export const ValidationStats = (props: TValidationStatsProps) => {
  const {
    questions,
    currentlyValidatingIndex,
    isCurrent = false,
    updateCurrent,
    validateAll,
    isValidating,
    isChapterContentReady
  } = props;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const displayQuestions = currentlyValidatingIndex === -1 ? questions : questions.slice(0, currentlyValidatingIndex);
  const validCount = displayQuestions.filter((q) => q.answer === "yes").length;
  const invalidCount = displayQuestions.filter((q) => q.answer === "no").length;
  const naCount = displayQuestions.filter((q) => q.answer === "na").length;
  const validateAllDisabled =
    isValidating ||
    questions.length === 0 ||
    (isCurrent && questions.every((q) => q.answer !== null)) ||
    !isChapterContentReady;
  const { closeHandler } = useProjectSidebarStore();

  return (
    <CardHeader
      data-testid="validation-stats"
      className="flex flex-col gap-2 border border-innoveraZinc-100 rounded-t-[8px] text-zinc-500 text-sm py-2 px-4"
    >
      <div className="flex items-center justify-between mb-2">
        <Typography variant="label2" className="text-zinc-900">
          Analysis
        </Typography>

        <div className="flex items-center gap-1">
          <CurrentStatusDropdown
            isCurrent={isCurrent}
            isValidationAll={isValidating}
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            updateCurrent={updateCurrent}
          />
          <AppButton appearance="text" size="icon" onClick={closeHandler}>
            <XIcon />
          </AppButton>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-[2px]">
            <CheckIcon className="w-[12] h-[12]" />
            <Typography
              variant="label3"
              className={cn(
                "text-innoveraZinc-500",
                validCount > 0 && "text-innoveraGreen-500 bg-innoveraGreen-50 py-[5px] px-[4px] rounded-sm"
              )}
            >
              {validCount > 0 && "+"}
              {validCount}
            </Typography>
          </div>
          <div className="flex items-center gap-[2px]">
            <XIcon className="w-[12] h-[12]" />
            <Typography
              variant="label3"
              className={cn(
                "text-innoveraZinc-500",
                invalidCount > 0 && "text-innoveraRed-400 bg-innoveraRed-50 py-[5px] px-[4px] rounded-sm"
              )}
            >
              {invalidCount > 0 && "+"}
              {invalidCount}
            </Typography>
          </div>
          <div className="flex items-center gap-[2px]">
            <ProhibitIcon className="w-[12] h-[12]" />
            <Typography
              variant="label3"
              className={cn("text-innoveraZinc-500", naCount > 0 && "bg-innoveraZinc-100 py-[5px] px-[4px] rounded-sm")}
            >
              {naCount > 0 && "+"}
              {naCount}
            </Typography>
          </div>
        </div>
        <AppButton
          data-testid="validate-all-button"
          onClick={validateAll}
          disabled={validateAllDisabled}
          size="sm"
          startAdornment={<SparkleIcon className="h-4 w-4 transition-transform duration-100" />}
          isLoading={isValidating}
          loadingText="Validating"
        >
          Validate
        </AppButton>
      </div>
    </CardHeader>
  );
};
